import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import { calculateWaitingTime, displayDate, displayValue, downloadCSV, formatDate, formatDateRange, formatPhoneNumber, getStatusColor, statusColors, ticketMapper } from "@/utils/Helper";
import { Checkbox } from "@/components/ui/CheckBox";
import { ColumnDef, getCoreRowModel, getFilteredRowModel, getSortedRowModel, Row, SortingState, useReactTable, VisibilityState } from "@tanstack/react-table";
import React, {useState} from "react";
import { useToast } from "@/components/ui/UseToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { ErrorType } from "@/app/types";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { Status } from "@/app/shared_enums/enums";
import { Badge } from "@/components/ui/Badge";
import { DataTableRowActions } from "./DataTableRowAction";
import { StatusFilter } from "@/constants/counter-management";
import { useGetDepartmentsQuery } from "@/services/DepartmentApi";
import { Department } from "@/app/types/department";
import { TicketStatus, useGetAllListingOfTicketsQuery, useGetTicketFiltersQuery } from "@/services/LiveQueueService";
import { Ticket } from "@/app/types/tickets";
import { DataTableViewOptions } from "./DataTableViewOption";
import { Table } from "@tanstack/react-table"; // Add this import
import { TicketStatusFilter } from "@/constants/ticket-listing/TicketFilter";
import { DatePickerWithRange } from "@/components/ui/date-range/DateRangePicker";
import DetailLogs from '../ticket-detail/AuditLog';
import { formatIncompletePhoneNumber } from "libphonenumber-js";
import TicketEditForm from "../ticket-detail/TicketDetailEdit";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import AddTicketForm from "./modal/AddTicketFromAdmin";
import { capitalizeFirstLetter } from "@/utils/Helper";
import axios from "axios";
import { useTicket } from "@/context/TicketContext";
const {VITE_PRINTER_URL } = import.meta.env;

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};
const TicketTable = () => {
  const { convertTicketFromRToS } = useTicket();

  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = React.useState<Ticket>();
  const [openLog, setOpenLog] = React.useState(false);
  const [ticketId, setTicketId] = React.useState<undefined | Number>();
  const tickets = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).tickets ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const AddTicket = (() => {
      try {
        return (
          JSON.parse(localStorage.getItem("accessLevels") as string)
            .hide_add_ticket ?? "no_access"
        );
      } catch {
        return "no_access";
      }
    })();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [openFilter, setOpenFilter] = React.useState(false);
  const [action, setAction] = React.useState<"add" | "edit">("add");
  interface FilterData {
    date_from?: Date;
    date_to?: Date;
    [key: string]: any;
  }
  const { data: staffData } = useGetStaffListQuery({ query: "" });
  const [filterData, setFilterData] = React.useState<FilterData>({});
  const [data, setData] = React.useState<Department | undefined>(undefined);
  const [inputValue, setInputValue] = React.useState<string>("");
  const { data: ticketFilterData } = useGetTicketFiltersQuery();
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);
    
  const [addTicketFormOpen, setAddTicketFormOpen] = useState(false);
  const {
    data: departmentData,
    isLoading: isDepartmentDataLoading,
    refetch,
    error,
    isError,
  } = useGetAllListingOfTicketsQuery(
    {
      query: query,
    },
    {
      skip: query == "",
    }
  );

  const tableData = React.useMemo(() => {
    return Array.isArray(departmentData?.data) ? departmentData?.data : [];
  }, [departmentData]);

  const [tableInstance, setTableInstance] =
    React.useState<Table<Ticket> | null>(null);

  const handleOpenForm = (payload: Department) => {
    setAction("edit");
    setData({ ...payload, id: payload.id });
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<Ticket> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={tickets}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
        setOpenLog={setOpenLog}
        setTicketId={setTicketId}
        setSheetOpen={setIsSheetOpen}
        setSelectedTicket={setSelectedTicket}
        handleBrowserPrint={handleBrowserPrint}
      />
    ),
  };

  const columns: ColumnDef<Ticket>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    // {
    //   id: "ticket_id",
    //   accessorKey: "Ticket No",
    //   meta: "Ticket No",
    //   header: ({ column }) => (
    //     <DataTableColumnHeader
    //       column={column}
    //       title="Ticket No"
    //       sortKey="ticket_id"
    //       searchCriteria={searchCriteria} // Pass current search criteria
    //       toggleSortOrder={toggleSortOrder}
    //     />
    //   ),
    //   cell: ({ row }) => {
    //     return (
    //       <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
    //         {row?.original.r_ticket_number
    //           ? `(${row.original.r_ticket_number}) -> `
    //           : ""}{" "}
    //         {displayValue(row?.original.ticket_number)}
    //       </div>
    //     );
    //   },
    // },
    {
      id: "ticket_number",
      accessorKey: "Ticket Id",
      meta: "Ticket Id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Ticket No"
          sortKey="ticket_id"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div
            onClick={() => {
              setTicketId(row.original?.id);
              setOpenLog(true);
            }}
            className="flex items-center cursor-pointer capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden underline"
          >
            {displayValue(row?.original.ticket_id)}
          </div>
        );
      },
    },
    {
      id: "created_at",
      accessorKey: "Ticket Date",
      meta: "Ticket Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Ticket Date"
          sortKey="created_at"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {new Date(row?.original.date)
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")}
          </div>
        );
      },
    },
    {
      id: "customer_id",
      accessorKey: "Customer ID",
      meta: "Customer ID",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Customer ID"
          sortKey="customer_id"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(
              Number(row?.original.customer.id) !== 0
                ? row.original.customer.id
                : ""
            )}{" "}
          </div>
        );
      },
    },
    {
      id: "customer_username",
      accessorKey: "Customer User Name",
      meta: "Customer User Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="ITS No"
          sortKey="customer_username"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(
              Number(row?.original.customer.id) !== 0
                ? row.original.customer.username
                : ""
            )}{" "}
          </div>
        );
      },
    },
    {
      id: "customer_name",
      accessorKey: "Customer Name",
      meta: "Customer Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Customer Name"
          sortKey="customer_name"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(
              Number(row?.original.customer.id) !== 0
                ? row.original.customer.name
                : ""
            )}{" "}
          </div>
        );
      },
    },
    {
      id: "contact_number",
      accessorKey: "Customer Number",
      meta: "Customer Number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Phone Number"
          sortKey="contact_number"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        // Check the length of the contact_number string
        const shouldShowNone = [0, 2, 3, 4].includes(
          row?.original.customer.phone_number?.length ?? 0
        );

        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {shouldShowNone
              ? ""
              : formatPhoneNumber(row?.original.customer.phone_number ?? "")}
          </div>
        );
      },
    },
    {
      id: "customer_email",
      accessorKey: "Customer Email",
      meta: "Customer Email",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Email Address"
          sortKey="customer_email"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex normal-case items-center  gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(
              Number(row?.original.customer.id) !== 0
                ? row.original.customer.email
                : ""
            )}{" "}
          </div>
        );
      },
    },

    {
      id: "department",
      accessorKey: "Department",
      meta: "Department",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Department"
          sortKey="department"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row.original.service.department_name)}{" "}
          </div>
        );
      },
    },
    {
      id: "service",
      accessorKey: "Service",
      meta: "Service",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Service"
          sortKey="service"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row.original.service.name)}{" "}
          </div>
        );
      },
    },
    {
      id: "counter",
      accessorKey: "Counter",
      meta: "Counter",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Counter"
          sortKey="counter"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue("Counter" + " " + row.original.counter_id)}
          </div>
        );
      },
    },
    {
      id: "wait_time",
      accessorKey: "Wait Time",
      meta: "Wait Time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Wait Time"
          sortKey="wait_time"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {row.original.served_at
                                ? (() => {
                                    const waitingMinutes = calculateWaitingTime(
                                      row.original?.date,
                                      row.original?.time,
                                      row.original.served_at ?? new Date()
                                    );
                                    return waitingMinutes >= 60
               ? `${Math.floor(waitingMinutes / 60)} hr${waitingMinutes % 60 !== 0 ? ` ${waitingMinutes % 60} min` : ""}`
               : waitingMinutes > 0
              ? `${waitingMinutes} min`
            : "0 min";
            })()
           : ""}
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "Status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <Badge
              className={`${getStatusColor(row.original?.status) || "bg-gray-500"} text-white font-medium text-xs`}
            >
              {displayValue(row?.original.status.replace(/_/g, " "))}
            </Badge>
          </div>
        );
      },
    },
    {
      id: "serving_agent",
      accessorKey: "Serving Agent",
      meta: "Serving Agent",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Serving Agent"
          sortKey="serving_agent"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.serving_agent?.name?.toString())}
          </div>
        );
      },
    },

    {
      accessorKey: "Last_Updated",
      meta: "Last Updated",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Last Updated"
          sortKey="updated_at"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {new Date(row?.original.updated_at)
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")}
          </div>
        );
      },
    },
    ...(tickets !== "read" ? [actionsColumn] : []),
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      ticket_id: true, // Ticket No
      customer_name: true, // Customer Name
      contact_number: true, // Customer Number
      customer_email: true, // Customer Email
      service: true, // Service
      counter: true, // Counter
      status: true, // Status
      customer_id: false, // Customer ID (hidden)
      customer_username: false, // Customer User Name (hidden)
      department: false, // Department (hidden)
      wait_time: false, // Wait Time (hidden)
      serving_agent: false, // Serving Agent (hidden)
      created_at: true, // Ticket Date (hidden)
      updated_at: false, // Last Updated (hidden)
      Last_Updated:false
      // Add any other columns you want to hide here
    });

  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: tableData as Ticket[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = departmentData?.filtered_count ?? 0;

  React.useEffect(() => {
    if (isError) {
      // error state
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "staff_id",
      label: "Assigned To",
      options: staffData,
      function: (value: string) => handleFilterChange("staff_id", value),
    },
    {
      type: "multiselect",
      name: "status",
      label: "Status",
      options: TicketStatusFilter,
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "department_ids",
      label: "Department",
      options: ticketFilterData?.departments.map((department) => ({
        value: department.id,
        label: department.name,
      })),
      function: (value: string) => handleFilterChange("department_ids", value),
    },
    {
      type: "multiselect",
      name: "service_ids",
      label: "Service",
      options: ticketFilterData?.services.map((services) => ({
        value: services.id,
        label: services.name,
      })),
      function: (value: string) => handleFilterChange("service_ids", value),
    },
  ];


  const handleDateRange = (dates: {
    date_from: Date | undefined;
    date_to: Date | undefined;
  }) => {
    const formattedStartDate = dates.date_from
      ? formatDateRange(dates.date_from)
      : "";
    const formattedEndDate = dates.date_to
      ? formatDateRange(dates.date_to)
      : "";

    setFilterData((prev) => ({
      ...prev,
      date_from: formattedStartDate,
      date_to: formattedEndDate,
    }));

    setSearchCriteria((prev: any) => ({
      ...prev,
      date_from: formattedStartDate || undefined,
      // Only set end_date if start_date is present
      date_to:
        formattedStartDate && !formattedEndDate ? undefined : formattedEndDate,
      offset: 0,
      sort_key: "id",
      sort_order: "desc",
    }));
  };
  const handleExportSelected = () => {
    if (!tableInstance) {
      console.error("Table instance is not initialized");
      return;
    }

    try {
      const selectedRows = tableInstance
        .getSelectedRowModel()
        .rows.map((row: Row<Ticket>) => row.original);

      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }


      const updatedSelectedRows = selectedRows.map((row: Ticket) => ({
        ...row,
      }));

      downloadCSV(updatedSelectedRows, "Tickets.csv", ticketMapper);
      // Add your export logic here using updatedSelectedRows
    } catch (error) {
      console.error("Error exporting selected rows:", error);
      toast({
        variant: "destructive",
        title: "An error occurred while exporting selected rows.",
      });
    }
  };

  const handleSaveTicket = async (updatedTicket:Ticket) => {
     // Implement your API call to save the ticket
     console.log("Saving ticket:", updatedTicket);
     // Example API call:
     // await updateTicket(updatedTicket);
     return true;
   };

   const handleRefetchData = () => {
     // Implement your data refetch logic
     console.log("Refetching tickets data");
   };

  const handleBrowserPrint = async (ticket: Ticket) => {
    // If already an S-series ticket, skip conversion
    const isServiceTicket = ticket.ticket_number?.startsWith("S-");
    let generatedTicket = undefined as any;

    if (!isServiceTicket) {
      try {
        generatedTicket = await convertTicketFromRToS(
          ticket.ticket_number,
          +ticket.service.id ? +ticket.service.id : 0,
          ticket.id,
          ticket.title,
          +ticket.customer.id ? +ticket.customer.id : 0,
          ticket.description,
          { throwOnError: true }
        );
      } catch (error) {
        const typedError = error as ErrorType;
        const errorMessage = typedError.data?.detail || typedError.data?.message || "Failed to generate service ticket";
        toast({
          variant: "destructive",
          title: "Print Failed",
          description: errorMessage,
        });
        return; // stop printing
      }
    }

    const closingTimeParts = ticket.service.closing_time?.split(":").map(Number);
    let formattedMessage;
    if (closingTimeParts.length < 2) return;
    const [hour, minute] = closingTimeParts;
    let closingTimeAdjusted = hour % 12 || 12;
    const period = hour >= 12 ? "PM" : "AM";
    const formattedClosingTime = `${closingTimeAdjusted}:${String(minute).padStart(2, "0")} ${period}`;
    const formattedCreatedTime = new Date(ticket.created_at).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });;

    formattedMessage = `
        Service Name: ${capitalizeFirstLetter(ticket.service.name)}
        Ticket Number: ${generatedTicket?.assigned_number || ticket.ticket_number}
        Counter Code: ${generatedTicket?.counter_code||generatedTicket?.counter_id || ticket.counter_id}
        Service Closing Time: ${formattedClosingTime}
        ${formattedCreatedTime}
    `;

    

    try {
      await axios.post(`${VITE_PRINTER_URL}`, {
        message: formattedMessage,
      });

      toast({
        title: "Success",
        description: "Ticket sent to the printer.",
      });
    } catch (printError) {
      console.error("Error sending to printer:", printError);
      // Fallback to browser printing
      // Create a temporary div for printing
      const printDiv = document.createElement('div');
      printDiv.id = 'print-content';
      
      // Apply direct styles
      Object.assign(printDiv.style, {
        fontFamily: 'Arial, sans-serif',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: 'white',
        width: '80mm',
        margin: '0 auto'
      });
      
      // Parse and format the content properly
      const lines = formattedMessage.trim().split('\n').map(line => line.trim());
      const formattedContent = lines.map(line => {
        // Special handling for Service Closing Time
        if (line.includes('Service Closing Time:')) {
          // The closing time already includes AM/PM from GenerateToken
          const timeValue = line.split('Service Closing Time:')[1].trim();
          return `
            <div style="margin: 8px 0; line-height: 1.5;">
              <span style="font-weight: bold;">Service Closing Time:</span>
              <span style="font-size: 1.1em;">${timeValue}</span>
            </div>
          `;
        }
        
        // Handle created time (date/time stamp)
        if (line.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
          return `<div style="margin: 8px 0; font-size: 1.1em;">${line}</div>`;
        }
        
        // Handle other lines with colons
        if (line.includes(':')) {
          const [label, value] = line.split(':').map(str => str.trim());
          return `
            <div style="margin: 8px 0; line-height: 1.5;">
              <span style="font-weight: bold;">${label}:</span>
              <span style="font-size: 1.1em;">${value}</span>
            </div>
          `;
        }
        
        // Handle other lines
        return `<div style="margin: 8px 0; font-size: 1.1em;">${line}</div>`;
      }).join('');
      
      printDiv.innerHTML = `
        <div style="border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px;">
          <h2 style="font-size: 1.2em; margin: 0;">Queue Ticket</h2>
        </div>
        ${formattedContent}
        <div style="border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px;">
          <small style="font-size: 0.8em;">Thank you for your patience</small>
        </div>
      `;
      
      // Remove any existing print content
      const existingPrintContent = document.getElementById('print-content');
      if (existingPrintContent) {
        existingPrintContent.remove();
      }
      
      document.body.appendChild(printDiv);
      
      setTimeout(() => {
        window.print();
        setTimeout(() => {
          document.body.removeChild(printDiv);
        }, 100);
      }, 100);
      toast({
        title: "Using browser print",
        description: "Printer not connected. Using browser print instead.",
      });
    }
  };

  return (
    <div className="w-full space-y-4 pt-2">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {" "}
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search Ticket"}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
        <div className="flex flex-wrap justify-start gap-2 md:gap-3 lg:flex-row lg:justify-end lg:items-end w-full">
          <div>
            <Button
              access={AddTicket === "write"}
              className="bg-primary text-sm text-nowrap  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
              onClick={() => setAddTicketFormOpen(true)} // Update to open the form
            >
              <i className="fa-solid fa-plus" />
              Create New
            </Button>
          </div>
          {/* Date Picker */}
          <div className="text-sm text-black flex items-center w-fit justify-center gap-1 h-8 md:text-xs  md:w-auto ">
            <DatePickerWithRange
              name="Date Range"
              value={{
                date_From: filterData.date_from,
                date_to: filterData.date_to,
              }}
              onValueChange={handleDateRange}
              label="Select date range"
              className="w-full md:w-auto text-gray-400 "
            />
          </div>

          {/* Reset Button */}
          <button
            className="text-xs md:text-sm border rounded-lg text-black border-primary flex items-center gap-1 h-8 px-2"
            onClick={() => {
              setFilterData({});
              setSearchCriteria(initialValue);
              setInputValue("");
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex gap-1 justify-center items-center">
                    <i className="fa-solid fa-arrows-rotate text-gray-400 "></i>
                    {/* <span className="hidden md:inline">Reset</span> */}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset page</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>

          {/* Export Button */}
          <DataTableViewOptions action={handleExportSelected} table={table} />

          {/* Filter Button */}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="capitalize">click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
        {/* Buttons Container */}
      </div>
      {/* Data Table */}

      <div className="mt-4 bg-white">
        <DataTable
          showColumnSettings={true}
          columns={columns}
          data={tableData}
          tableInstance={table}
          isLoading={isDepartmentDataLoading}
          searchMessage="No Tickets Found"
          onTableInit={setTableInstance}
        />{" "}
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        setInputValue={setInputValue}
        filterDisplay={filterDisplay}
      />
      <DetailLogs isOpen={openLog} setOpen={setOpenLog} ticketId={ticketId} />


        <TicketEditForm
              isOpen={isSheetOpen}
              setOpen={setIsSheetOpen}
              initialData={selectedTicket}
              onSave={handleSaveTicket}
              refetch={handleRefetchData}
            />
      <AddTicketForm
        isOpen={addTicketFormOpen}
        setOpen={setAddTicketFormOpen}
        refetch={refetch}
      />
    </div>
  );
};

export default TicketTable;
